import { useEffect, useState } from "react";
import {
    FiDollarSign,
    FiCreditCard,
    FiTrendingUp,
    FiUsers,
    FiSearch,
    FiCheckCircle,
    FiClock,
    FiXCircle,
    FiLoader,
} from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAllOrders } from "../../store/smileDesign/paymentSlice";
import { useDebouncedCallback } from "use-debounce";
import TablePagination from "../../components/smileDesign/TablePagination";

const statusConfig: Record<
    string,
    { className: string; icon: React.ReactNode; label: string }
> = {
    PAID: {
        className: "bg-green-500/10 border-green-500/20 text-green-400",
        icon: <FiCheckCircle />,
        label: "Paid",
    },
    PENDING: {
        className: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
        icon: <FiClock />,
        label: "Pending",
    },
    FAILED: {
        className: "bg-red-500/10 border-red-500/20 text-red-400",
        icon: <FiXCircle />,
        label: "Failed",
    },
    REFUNDED: {
        className: "bg-purple-500/10 border-purple-500/20 text-purple-400",
        icon: <FiCreditCard />,
        label: "Refunded",
    },
};

const PaymentManager = () => {
    const dispatch = useAppDispatch();
    const { allOrders, allOrdersPagination, stats, loading } = useAppSelector(
        (state) => state.payment
    );
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const loadOrders = (overrides?: { page?: number; search?: string }) => {
        dispatch(
            fetchAllOrders({
                page: overrides?.page ?? page,
                search: (overrides?.search ?? search) || undefined,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadOrders({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadOrders();
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const formatRevenue = (amount: number) => {
        if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
        return amount.toFixed(2);
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-8">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                            Payment Manager
                        </h1>
                        <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                            Monitor transactions, payments, and billing records.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Total Revenue</p>
                                <h2 className="text-3xl font-bold text-green-400 mt-2">
                                    {formatRevenue(stats?.totalRevenue ?? 0)}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400">
                                <FiDollarSign className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Transactions</p>
                                <h2 className="text-3xl font-bold text-cyan-400 mt-2">
                                    {stats?.totalTransactions ?? 0}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                <FiCreditCard className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Paid Orders</p>
                                <h2 className="text-3xl font-bold text-yellow-400 mt-2">
                                    {stats?.paidTransactions ?? 0}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-400">
                                <FiTrendingUp className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Customers</p>
                                <h2 className="text-3xl font-bold text-purple-400 mt-2">
                                    {stats?.uniqueCustomers ?? 0}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                <FiUsers className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative mb-6">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                    <input
                        type="text"
                        placeholder="Search by order, package, user, or email..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                </div>

                {loading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!loading && allOrders.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiCreditCard className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">No payments yet</h3>
                    </div>
                )}

                {!loading && allOrders.length > 0 && (
                    <>
                        <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-[#080808] border-b border-[#1f2221]">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Order</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Customer</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Package</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Amount</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Credits</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Status</th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allOrders.map((payment) => {
                                            const status = statusConfig[payment.status] ?? statusConfig.PENDING;
                                            return (
                                                <tr
                                                    key={payment.order_id}
                                                    className="border-b border-[#1f2221] hover:bg-[#151716] transition-all duration-200"
                                                >
                                                    <td className="px-6 py-5 font-medium text-cyan-400">
                                                        #{payment.order_id}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <h3 className="font-semibold text-white">
                                                            {payment.user_name}
                                                        </h3>
                                                        <p className="text-[#8b8f8d] text-sm">
                                                            {payment.email}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-5 text-[#b7bab8]">
                                                        {payment.package_name}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span className="text-green-400 font-bold text-lg">
                                                            {payment.currency}
                                                            {payment.amount}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5 text-cyan-400">
                                                        {payment.total_credits ??
                                                            payment.credits + payment.bonus_credits}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span
                                                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${status.className}`}
                                                        >
                                                            {status.icon}
                                                            {status.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5 text-[#8b8f8d]">
                                                        {new Date(payment.created_at).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="lg:hidden space-y-4">
                            {allOrders.map((payment) => {
                                const status = statusConfig[payment.status] ?? statusConfig.PENDING;
                                return (
                                    <div
                                        key={payment.order_id}
                                        className="bg-[#101211] border border-[#1f2221] rounded-3xl p-5"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <p className="text-cyan-400 font-semibold">
                                                    #{payment.order_id}
                                                </p>
                                                <h3 className="font-semibold text-white mt-2">
                                                    {payment.user_name}
                                                </h3>
                                                <p className="text-[#8b8f8d] text-sm break-all">
                                                    {payment.email}
                                                </p>
                                            </div>
                                            <span
                                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${status.className}`}
                                            >
                                                {status.label}
                                            </span>
                                        </div>
                                        <div className="mt-5 space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-[#8b8f8d]">Package</span>
                                                <span className="text-white">{payment.package_name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#8b8f8d]">Amount</span>
                                                <span className="text-green-400 font-bold">
                                                    {payment.currency}
                                                    {payment.amount}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <TablePagination
                            pagination={allOrdersPagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadOrders({ page: nextPage });
                            }}
                            itemLabel="orders"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentManager;
